"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import AppLogo from "@/components/ui/AppLogo";


const navLinks = [
  { href: "/", label: "Home" },
  { href: "/features", label: "Features" },
  { href: "/pricing", label: "Pricing" },
  { href: "/self-hosted", label: "Self-Hosted" },
];

export default function Header() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "py-3 bg-bg-primary/90 backdrop-blur-xl shadow-[0_4px_24px_rgba(0,0,0,0.4)]"
            : "py-6 bg-transparent"
        }`}
      >
        <div className="max-w-container mx-auto px-6 md:px-8 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 group">
            <AppLogo
              size={64}
              iconName="SparklesIcon"
              text="TapTale"
              className="group-hover:opacity-90 transition-opacity"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            {navLinks?.map((link) => (
              <Link
                key={link?.href}
                href={link?.href}
                className={`nav-link text-sm font-medium transition-colors duration-300 pb-0.5 ${
                  pathname === link?.href
                    ? "text-white active" :"text-gray-400 hover:text-white"
                }`}
              >
                {link?.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              href="/pricing"
              className="btn-lift inline-flex items-center justify-center gap-2 bg-accent-crimson hover:bg-accent-crimson-hover text-white font-semibold text-sm px-7 py-3.5 rounded-full transition-colors"
            >
              <span>Order Starter Kit</span>
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden flex flex-col gap-1.5 p-2 group"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
          >
            <span
              className={`block h-px bg-white transition-all duration-500 ease-out ${
                menuOpen ? "w-6 rotate-45 translate-y-2" : "w-6 group-hover:w-4"
              }`}
            />
            <span
              className={`block h-px bg-white transition-all duration-500 ease-out delay-75 ${
                menuOpen ? "opacity-0 w-4" : "w-4 group-hover:w-6"
              }`}
            />
            <span
              className={`block h-px bg-white transition-all duration-500 ease-out ${
                menuOpen ? "w-6 -rotate-45 -translate-y-2.5" : "w-6 group-hover:w-4"
              }`}
            />
          </button>
        </div>
      </header>
      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-bg-primary/95 backdrop-blur-xl transition-all duration-500 md:hidden flex flex-col ${
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!menuOpen}
      >
        <div className="flex-1 flex flex-col items-center justify-center gap-8 px-6 pt-20">
          {navLinks?.map((link, i) => (
            <Link
              key={link?.href}
              href={link?.href}
              onClick={() => setMenuOpen(false)}
              className={`font-serif text-4xl font-light italic transition-all duration-300 ${
                pathname === link?.href
                  ? "text-accent-crimson" :"text-white/70 hover:text-white"
              }`}
              style={{ transitionDelay: menuOpen ? `${i * 80}ms` : "0ms" }}
            >
              {link?.label}
            </Link>
          ))}
          <div className="mt-8 flex flex-col gap-4 w-full max-w-xs">
            <Link
              href="/pricing"
              onClick={() => setMenuOpen(false)}
              className="w-full text-center py-3.5 rounded-full border border-white/20 text-white font-semibold text-sm"
            >
              Sign In
            </Link>
            <Link
              href="/pricing"
              onClick={() => setMenuOpen(false)}
              className="w-full text-center py-3.5 rounded-full bg-accent-crimson text-white font-semibold text-sm"
            >
              Order Starter Kit
            </Link>
          </div>
        </div>
        <div className="pb-8 text-center text-gray-600 text-xs font-mono">
          Every treasure has a tale
        </div>
      </div>
    </>
  );
}